import { LimitInformation } from '@simOn/ui/sim-data-usage';
import { AgentActionDto } from './agent-action.model';

/**
 * Contract of `/api/Agents/*` (see `swagger.json`, tag `Agents`).
 * Only the non-streaming flow is available; `SendMessageStream` is intentionally not modelled here.
 */

/** Numeric role returned by the backend (`AgentMessageRole` in swagger). */
export enum AgentMessageRole {
  Unknown = 0,
  User = 1,
  Assistant = 2,
  System = 3,
  Tool = 4
}

/**
 * Token usage of one whole `SendMessage` call (every model call and tool round of the message);
 * `null` for history messages.
 */
export interface AgentUsageDto {
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  /** The only non-overlapping number; the backend enforces limits on it. */
  totalTokens: number;
}

/** One message returned by `SendMessage` / inside `GetConversation`. */
export interface AgentMessageDto {
  /** May be an empty GUID for history messages loaded from Foundry. */
  id: string;
  /** Declared as a number in swagger; tolerated as the enum name too (e.g. "User") in case of string enum serialization. */
  role: AgentMessageRole | string;
  content: string | null;
  /** Actions the user may trigger from the message; present on assistant messages only. */
  actions?: AgentActionDto[] | null;
  usage?: AgentUsageDto | null;
  /** May be a default date for history messages loaded from Foundry. */
  createdAt: string;
}

/** Entry of `GET /api/Agents/GetConversations`. */
export interface AgentConversationListEntryDto {
  id: string;
  title: string | null;
  createdAt: string;
  editedAt: string | null;
}

/** Response of `CreateConversation` and `GetConversation`. */
export interface AgentConversationDto extends AgentConversationListEntryDto {
  messages: AgentMessageDto[] | null;
}

/** Payload of `POST /api/Agents/CreateConversation`. */
export interface CreateAgentConversationDto {
  title?: string | null;
}

/** Grounding context sent with every message; the agent is not scoped by the apartment header. */
export interface AgentMessageContextDto {
  apartmentId?: string | null;
  /** Present in the DTO but not used by any backend tool yet. */
  workspaceId?: string | null;
  /** IANA time zone id (e.g. "Europe/Warsaw"); relative dates in the message are resolved in it. Invalid ids give 400. */
  timeZone?: string | null;
  /** UI language as a BCP 47 tag (e.g. "pl", "en-US"); a hint, not a forced answer language. Invalid tags give 400. */
  language?: string | null;
}

/** Payload of `POST /api/Agents/SendMessage`. */
export interface SendAgentMessageDto {
  conversationId: string;
  message: string;
  context?: AgentMessageContextDto | null;
}

/**
 * Response of `GET /api/Agents/GetUserStatus`: whether the Simlab office enabled the assistant for the user
 * and how many tokens are left. Monthly tokens are used first, purchased tokens never expire.
 */
export interface AgentUserStatusDto {
  isEnabled: boolean;
  monthlyTokens: LimitInformation;
  /** `used` is capped at `limit`. */
  purchasedTokens: LimitInformation;
  /** Both pools together, never negative; `SendMessage` fails with `AiUsageLimitReached` at 0. */
  remainingTokens: number;
}
